<script setup lang="ts">
import type { OrganizationJs } from "@/generated/models";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { useAxureCloudConfig } from "ls/state/useAxureCloudConfig";
import { CheckIcon } from "lucide-vue-next";
import { computed } from "vue";
import { useAccessLevelFromOrganization } from "../useOrganizationAccessLevel";
import { useSeatsAndLicensesInfo } from "../useSeatsAndLicensesInfo";

const props = defineProps<{
  organization: OrganizationJs;
  isOnBillingPage?: boolean;
}>();

const { isAdmin } = useAccessLevelFromOrganization(() => props.organization);

const seatsAndLicensesInfo = useSeatsAndLicensesInfo(() => props.organization.Id);

const config = useAxureCloudConfig();
const buyNewSubscriptionUrl = computed(() => {
  const portalUrl = config.value?.PortalUrl;
  if (!portalUrl) return "";

  const url = new URL("/buy", portalUrl);
  url.searchParams.set("type", "2");
  url.searchParams.set("orgId", props.organization.Id);

  if (seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value
    && seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value > 0) {
    url.searchParams.set("authorQty", `${seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value}`);
  }

  return url.href;
});
</script>

<template>
  <div class="h-full max-w-64 flex-1 rounded-sm border border-slate-200 bg-white p-4">
    <div class="mb-4 flex">
      <label class="text-base font-semibold text-blue-600">
        Cloud Plus
      </label>
    </div>
    <div class="mb-6 flex gap-2">
      <div class="flex text-3xl leading-none font-semibold whitespace-nowrap">
        $5
      </div>
      <div class="self-end leading-snug text-slate-500">
        author/month
      </div>
    </div>

    <div class="mb-12">
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Everything in free
        </div>
      </div>
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited whiteboards
        </div>
      </div>
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited documents
        </div>
      </div>
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited templates
        </div>
      </div>
    </div>
    <template v-if="isAdmin">
      <SimpleButton size="large" class="w-full" :href="buyNewSubscriptionUrl" target="_blank">
        Upgrade to Cloud Plus
      </SimpleButton>
    </template>
    <template v-else-if="!props.isOnBillingPage">
      <div class="mb-4 rounded-sm border border-slate-300 p-4 text-center text-slate-500">
        Ask an admin to upgrade to Cloud Plus
      </div>
    </template>
  </div>
</template>
